import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, type AppRole } from "@/contexts/AuthContext";
import { getTourSteps, deltaTours, type TourStep, type Plan } from "@/data/tourSteps";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

const TOUR_AUTO_LAUNCH_SESSION_KEY = "nursly_tour_auto_launched";

interface OnboardingTourProps {
  /** If set, start from this specific step ID (for replay) */
  replayFromStep?: string | null;
  /** If set, run a delta tour by key instead of the main tour */
  deltaKey?: string | null;
  onClose: () => void;
}

async function markTourCompleted(userId: string) {
  try {
    await supabase
      .from("profiles")
      .update({
        onboarding_tour_completed: true,
        onboarding_tour_completed_at: new Date().toISOString(),
      })
      .eq("user_id", userId);
  } catch {
    // best-effort; tour will simply replay if it fails
  }
}

async function markDeltaTourCompleted(userId: string, currentMap: Record<string, string>, key: string) {
  try {
    const next = { ...currentMap, [key]: new Date().toISOString() };
    await supabase
      .from("profiles")
      .update({ delta_tours_completed: next })
      .eq("user_id", userId);
  } catch {
    // best-effort
  }
}

export function OnboardingTour({ replayFromStep, deltaKey, onClose }: OnboardingTourProps) {
  const { role, profile, user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const firstName = profile?.first_name || "there";
  const plan: Plan = (profile?.nursery_plan as Plan) || "professional";

  const delta = deltaKey ? deltaTours.find(d => d.key === deltaKey) : null;
  const steps = delta
    ? delta.steps
    : role
      ? getTourSteps(plan, role)
      : [];
  const totalSteps = steps.length;

  const initialStep = replayFromStep
    ? Math.max(0, steps.findIndex(s => s.id === replayFromStep))
    : 0;

  const [currentIndex, setCurrentIndex] = useState(initialStep);
  const [skippedSteps, setSkippedSteps] = useState<Set<string>>(new Set());
  const [showConfetti, setShowConfetti] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);

  const step = steps[currentIndex] as TourStep | undefined;

  // Update spotlight position
  useEffect(() => {
    if (!step?.targetSelector) {
      setSpotlightRect(null);
      return;
    }
    const updateRect = () => {
      const el = document.querySelector(step.targetSelector!);
      if (el) {
        setSpotlightRect(el.getBoundingClientRect());
      } else {
        setSpotlightRect(null);
      }
    };
    const timer = setTimeout(updateRect, 300);
    window.addEventListener("resize", updateRect);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateRect);
    };
  }, [step?.targetSelector, currentIndex]);

  // Navigate to step route if needed
  useEffect(() => {
    if (step?.navigateTo) {
      navigate(step.navigateTo);
    }
  }, [step?.navigateTo, navigate, currentIndex]);

  const persistCompletion = useCallback(async () => {
    if (!user?.id) return;
    if (delta) {
      await markDeltaTourCompleted(user.id, profile?.delta_tours_completed ?? {}, delta.key);
    } else {
      await markTourCompleted(user.id);
    }
    await refreshProfile();
  }, [user?.id, delta, profile?.delta_tours_completed, refreshProfile]);

  const goNext = useCallback(() => {
    if (currentIndex >= totalSteps - 1) {
      void persistCompletion();
      setShowConfetti(true);
      setTimeout(() => {
        onClose();
        if (!delta) navigate("/dashboard");
      }, 3000);
      return;
    }
    setCurrentIndex(currentIndex + 1);
  }, [currentIndex, totalSteps, persistCompletion, onClose, navigate, delta]);

  const skipStep = useCallback(() => {
    if (step) setSkippedSteps(prev => new Set(prev).add(step.id));
    goNext();
  }, [step, goNext]);

  const handleClose = () => setShowExitConfirm(true);
  const confirmExit = () => {
    // Mark the tour as completed in Supabase so it never re-triggers on
    // another device or browser. Completion lives on the profile row (single
    // source of truth), never localStorage, which is per-device.
    void persistCompletion();
    onClose();
  };

  if (!step || totalSteps === 0) return null;

  const progressPct = ((currentIndex + 1) / totalSteps) * 100;
  const content = step.content.replace("{firstName}", firstName);
  const isFinalStep = step.id.endsWith("-done");

  // Confetti screen
  if (showConfetti) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70">
        <div className="bg-card rounded-2xl p-8 max-w-md text-center shadow-2xl animate-in fade-in zoom-in-95 duration-500">
          <div className="text-6xl mb-4">🎓</div>
          <h2 className="text-2xl font-bold font-display mb-2">Tour Complete!</h2>
          <p className="text-muted-foreground mb-6">
            {firstName}, you've officially completed your Nursly platform tour! You're ready to hit the ground running.
          </p>
          {/* Simple confetti dots */}
          <div className="flex justify-center gap-1 mb-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full animate-bounce"
                style={{
                  backgroundColor: ["hsl(var(--primary))", "hsl(var(--info))", "hsl(var(--success))", "hsl(var(--warning))"][i % 4],
                  animationDelay: `${i * 0.1}s`,
                  animationDuration: "0.8s",
                }}
              />
            ))}
          </div>
          <Button onClick={() => { onClose(); navigate("/dashboard"); }} size="lg">
            Start using Nursly →
          </Button>
        </div>
      </div>
    );
  }

  // Exit confirmation
  if (showExitConfirm) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70">
        <div className="bg-card rounded-xl p-6 max-w-sm shadow-2xl">
          <h3 className="font-semibold mb-2">Leave the tour?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Are you sure you want to leave the tour? You can come back to it anytime from the ❓ Help menu at the bottom of the sidebar.
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowExitConfirm(false)}>
              Keep going
            </Button>
            <Button variant="secondary" onClick={confirmExit}>
              Leave tour
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Compute tooltip placement (fixed positioning, never off-screen).
  const viewportW = typeof window !== "undefined" ? window.innerWidth : 1024;
  const viewportH = typeof window !== "undefined" ? window.innerHeight : 768;
  const cardW = Math.min(420, viewportW - 32);
  const cardH = 280;
  let tooltipStyle: React.CSSProperties;
  if (spotlightRect) {
    const preferTop = spotlightRect.bottom + 16 + cardH > viewportH;
    const top = preferTop
      ? Math.max(16, spotlightRect.top - cardH - 16)
      : Math.min(spotlightRect.bottom + 16, viewportH - cardH - 16);
    const left = Math.max(16, Math.min(spotlightRect.left, viewportW - cardW - 16));
    tooltipStyle = { position: "fixed", top, left, width: cardW };
  } else {
    tooltipStyle = {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: cardW,
    };
  }

  return (
    <>
      {/* Dark overlay (single full-screen div). Spotlight cutout via box-shadow. */}
      <div
        className="fixed inset-0 z-[9998]"
        onClick={e => {
          // Allow clicks through to the spotlighted element; otherwise block.
          if (!spotlightRect) e.stopPropagation();
        }}
        style={{ pointerEvents: spotlightRect ? "none" : "auto" }}
      >
        {spotlightRect ? (
          <div
            style={{
              position: "absolute",
              top: spotlightRect.top - 8,
              left: spotlightRect.left - 8,
              width: spotlightRect.width + 16,
              height: spotlightRect.height + 16,
              borderRadius: 8,
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)",
              pointerEvents: "none",
            }}
          />
        ) : (
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.6)" }} />
        )}
      </div>

      {/* Tooltip card, fixed, above overlay, always interactive */}
      <div
        className={cn(
          "z-[9999] bg-card text-card-foreground rounded-xl shadow-2xl border p-5",
          "animate-in fade-in slide-in-from-bottom-4 duration-300"
        )}
        style={{ ...tooltipStyle, pointerEvents: "auto" }}
      >
        {/* Progress bar */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-muted-foreground font-medium">
            Step {currentIndex + 1} of {totalSteps}
          </span>
          <button onClick={handleClose} className="p-1 rounded hover:bg-muted" aria-label="Close tour">
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </div>
        <Progress value={progressPct} className="h-1.5 mb-4" />

        {/* Content */}
        <div className="flex items-start gap-2 mb-4">
          <span className="text-2xl">{step.emoji}</span>
          <div>
            <h3 className="font-bold font-display text-base mb-1">{step.title}</h3>
            {content.split("\n").map((line, i) => (
              <p key={i} className={cn("text-sm leading-relaxed", !line.trim() && "h-2")}>
                {line}
              </p>
            ))}
          </div>
        </div>

        {/* Interactive hint */}
        {step.interactiveHint && (
          <div className="bg-muted/50 rounded-lg px-3 py-2 mb-3 text-xs text-muted-foreground">
            👆 {step.interactiveHint}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleClose}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip tour
          </button>
          <Button onClick={goNext} size="sm">
            {isFinalStep ? "Start using Nursly →" : "Next →"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ─── Hook to manage tour visibility ───

export function useTourManager() {
  const { role, profile } = useAuth();
  const location = useLocation();
  const [showTour, setShowTour] = useState(false);
  const [replayStep, setReplayStep] = useState<string | null>(null);
  const [activeDeltaKey, setActiveDeltaKey] = useState<string | null>(null);
  const hasChecked = useRef(false);

  // B2-01: The auto-launching first-login tour has been removed. A guided
  // walkthrough of an empty platform delivered no real value; new users now
  // see the "Get started" dashboard checklist instead. Manual replay from the
  // Help menu still works, and delta tours still auto-launch for existing
  // users who already completed onboarding.
  useEffect(() => {
    if (!role || !profile || hasChecked.current) return;
    if (location.pathname === "/help") return;

    const autoLaunchKey = `${TOUR_AUTO_LAUNCH_SESSION_KEY}_${role}`;
    if (sessionStorage.getItem(autoLaunchKey) === "1") {
      hasChecked.current = true;
      return;
    }

    hasChecked.current = true;

    // Only auto-launch delta tours for users who already finished onboarding,
    // never the main tour on first login.
    if (!profile.onboarding_tour_completed) return;

    const plan = (profile.nursery_plan as Plan) || "professional";
    const completedMap = profile.delta_tours_completed ?? {};
    const pending = deltaTours.find(d => !completedMap[d.key] && d.appliesTo(plan, role));
    if (pending) {
      const timer = window.setTimeout(() => {
        setActiveDeltaKey(pending.key);
        setShowTour(true);
        sessionStorage.setItem(autoLaunchKey, "1");
      }, 1500);
      return () => window.clearTimeout(timer);
    }
  }, [role, profile, location.pathname]);


  const startTour = useCallback((fromStep?: string) => {
    setActiveDeltaKey(null);
    setReplayStep(fromStep || null);
    setShowTour(true);
  }, []);

  const startDeltaTour = useCallback((key: string) => {
    setReplayStep(null);
    setActiveDeltaKey(key);
    setShowTour(true);
  }, []);

  const closeTour = useCallback(() => {
    setShowTour(false);
    setReplayStep(null);
    setActiveDeltaKey(null);
  }, []);

  return { showTour, replayStep, deltaKey: activeDeltaKey, startTour, startDeltaTour, closeTour };
}
